import React, { useEffect, useMemo, useState } from 'react';
import './styles';
import { useSelector } from 'react-redux';
import UILoadingPage from '../../../../components/UI/UILoadingPage/UILoadingPage';
import {
	AGENT_ADMIN_TAB,
	AGENT_BLOCKED_TECHNICIANS_TAB,
	AGENT_INFO_TAB,
	AGENT_LISTINGS_TAB,
	AGENT_PROMOTIONS_TAB,
	AGENT_SERVICES_TAB
} from '../../../../utils/Config/MenuBodyTabs';
import Helmet from 'react-helmet';
import UIMenuBody, { filterSelectedItem } from '../../../../components/UI/UIMenuBody';
import PLImage from '../../../../components/PL/PLImage';
import UIBookingButton from '../../../../components/UI/UIBookingButton';
import PLPermissionBoundary from '../../../../components/Boundaries/PLPermissionBoundary/PLPermissionBoundary';
import IndividualAgentBlockedTechs from './IndividualAgentBlockedTechs';
import IndividualAgentAdmin from './IndividualAgentAdmin';
import IndividualAgentListings from './IndividualAgentListings';
import IndividualAgentInfo from './IndividualAgentInfo';
import IndividualAgentPromotions from './IndividualAgentPromotions';
import { getIsUserAdminSelector } from '../../../../redux/selectors/CurrentUserSelectors';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { useLazyQuery } from '@apollo/react-hooks';
import { GET_AGENT_INFO } from './IndividualAgentInfo/queries';
import IndividualAgentServices from './IndividualAgentServices';

export default function IndividualAgent({}: IndividualAgentProps) {
	const [agent, setAgent] = useState<UserType>();
	const isUserAdmin = useSelector(getIsUserAdminSelector);

	const location = useLocation();
	const match = useRouteMatch();

	const [getAgent, { data: agentId }] = useLazyQuery(GET_AGENT_INFO, {
		variables: { id: match.params['id'] }
	});

	useEffect(() => {
		(async () => {
			await getAgent();
		})();
	}, []);

	useEffect(() => {
		(() => {
			if (agentId) {
				const fetchedAgent: UserType = agentId['user'];
				setAgent(fetchedAgent);
			}
		})();
	}, [agentId]);

	const menuHeader = useMemo(() => {
		if (!isUserAdmin || !agent) return null;
		return (
			<div className='individualAgent__menu-header'>
				<div className='individualAgent__menu-header--agent'>
					<PLImage imageClassName='individualAgent__menu-header--agent-img' src={agent.image} alt={'agent image'} />
					<p className='individualAgent__menu-header--name'>{agent.name}</p>
				</div>
				<div className='pl-separator' />
				<UIBookingButton bookingClient={agent} />
				<div className='pl-separator' />
			</div>
		);
	}, [isUserAdmin, agent]);

	const helmet = useMemo(() => {
		if (agent) {
			return (
				<Helmet>
					<title>{agent.name || 'Agent'} | Pineapple Labs</title>
					<meta property='og:title' content={`${agent.name} | Pineapple Labs`} />
				</Helmet>
			);
		}
		return null;
	}, [agent]);

	if (!agent) return null;

	const tabs = [AGENT_LISTINGS_TAB, AGENT_SERVICES_TAB, AGENT_INFO_TAB, AGENT_BLOCKED_TECHNICIANS_TAB, AGENT_PROMOTIONS_TAB, AGENT_ADMIN_TAB];

	const renderSelectedPage = () => {
		const agentId = match.params['id'];
		const selectedPage = filterSelectedItem(tabs, location);

		switch (selectedPage) {
			case AGENT_INFO_TAB:
				return <IndividualAgentInfo agentId={agentId} />;
			case AGENT_BLOCKED_TECHNICIANS_TAB:
				return <IndividualAgentBlockedTechs agentId={agentId} />;
			case AGENT_PROMOTIONS_TAB:
				return <IndividualAgentPromotions agentId={agentId} />;
			case AGENT_LISTINGS_TAB:
				return <IndividualAgentListings agentId={agentId} />;
			case AGENT_SERVICES_TAB:
				return <IndividualAgentServices agentId={agentId} />;
			case AGENT_ADMIN_TAB:
				return <IndividualAgentAdmin agentId={agentId} />;
			default:
				return null;
		}
	};

	if (!agent) {
		return <UILoadingPage key={agent} />;
	}

	return (
		<PLPermissionBoundary isGranted={isUserAdmin} className={`uiController`}>
			<UIMenuBody menuHeader={menuHeader} menuItems={tabs}>
				<>
					{helmet}
					{renderSelectedPage()}
				</>
			</UIMenuBody>
		</PLPermissionBoundary>
	);
}
